import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, ChevronRight } from "lucide-react";

const LEVEL_STYLES = {
  rouge: "border-l-4 border-red-500 bg-red-50/50",
  orange: "border-l-4 border-amber-500 bg-amber-50/50",
  vert: "border-l-4 border-emerald-500 bg-emerald-50/50",
};

const LEVEL_PILL = {
  rouge: "bg-red-100 text-red-800",
  orange: "bg-amber-100 text-amber-800",
  vert: "bg-emerald-100 text-emerald-800",
};

const KIND_LABEL = {
  facture: "Facture",
  demarche: "Démarche",
  fmob: "Franchi'Mob",
  publication: "Publication",
};

const PRIORITY_LABEL = {
  haute: "Priorité haute",
  moyenne: "Priorité moyenne",
  faible: "Priorité faible",
};

export default function Assistant() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/assistant/today").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-sm text-gray-500" data-testid="assist-loading">Chargement…</div>;

  // Group by priority
  const groups = { haute: [], moyenne: [], faible: [] };
  (data.items || []).forEach((it) => { groups[it.priority || "faible"].push(it); });

  const counts = {
    haute: groups.haute.length,
    moyenne: groups.moyenne.length,
    faible: groups.faible.length,
  };

  return (
    <div className="space-y-6" data-testid="assistant-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">Mode assistante</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Actions du jour</h1>
          <p className="text-sm text-gray-600 mt-1">Trié automatiquement par priorité — concentre-toi sur le rouge.</p>
        </div>
        <div className="flex gap-2">
          <div className="card-flat px-4 py-2 text-center">
            <div className="kpi-number text-xl text-red-700">{counts.haute}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Haute</div>
          </div>
          <div className="card-flat px-4 py-2 text-center">
            <div className="kpi-number text-xl text-amber-700">{counts.moyenne}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Moyenne</div>
          </div>
          <div className="card-flat px-4 py-2 text-center">
            <div className="kpi-number text-xl text-emerald-700">{counts.faible}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Faible</div>
          </div>
        </div>
      </div>

      {["haute", "moyenne", "faible"].map((prio) => (
        <section key={prio} data-testid={`assist-prio-${prio}`}>
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-2 h-2 rounded-full ${prio === "haute" ? "bg-red-500" : prio === "moyenne" ? "bg-amber-500" : "bg-emerald-500"}`} />
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-600">{PRIORITY_LABEL[prio]}</h3>
            <span className="text-xs font-bold text-gray-400">{groups[prio].length}</span>
          </div>
          {groups[prio].length === 0 ? (
            <div className="text-sm text-gray-400 italic pl-4">Rien à signaler.</div>
          ) : (
            <div className="space-y-2">
              {groups[prio].map((it, i) => {
                const Wrapper = it.project_id ? Link : "div";
                const props = it.project_id ? { to: `/projets/${it.project_id}` } : {};
                return (
                  <Wrapper
                    key={i}
                    {...props}
                    data-testid={`assist-item-${prio}-${i}`}
                    className={`flex items-center gap-3 p-3 rounded-md ${LEVEL_STYLES[it.level]} ${it.project_id ? "hover:shadow-sm transition cursor-pointer" : ""}`}
                  >
                    <span className={`pill ${LEVEL_PILL[it.level]} text-[10px] font-bold uppercase`}>{KIND_LABEL[it.kind] || it.kind}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{it.label}</div>
                      <div className="text-xs text-gray-600 truncate">{it.context}</div>
                    </div>
                    {it.project_id && <ChevronRight size={14} className="text-gray-400" />}
                  </Wrapper>
                );
              })}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
